import {useLingui} from '@lingui/react/macro';
import {useCallback, useRef} from 'react';
import {useNavigate, useParams} from 'react-router';
import {useSelector} from 'react-redux';
import {StyleSheet, View} from 'react-native';
import {Svg, Path} from 'react-native-svg';
import {useDragDropTarget} from 'interface/hooks/useDragDropTarget';
import {IconButton} from 'interface/base/IconButton';
import {DropFrame} from 'interface/base/DropFrame';
import {openPage} from 'utils/mediafire';
import {isTouch, isUpsellDeniedPlatform} from 'utils/platform';
import {colors, vectors} from 'config/styles';

import * as app from 'store/slices/app';
import * as files from 'store/slices/files';

export function MenuNavigation() {
  const {t} = useLingui();
  const dndRef = useRef<View>(null);
  const navigate = useNavigate();
  const {folderKey} = useParams<"folderKey">();
  const isPromoDisabled = isUpsellDeniedPlatform();
  const isPremium = useSelector(app.selectors.isPremium);
  const email = useSelector(app.selectors.getEmail);
  const trash = useSelector(files.selectors.getItem('trash'));
  const trashView = useSelector(files.selectors.getView('trash'));
  const isFollowing = folderKey === 'following';
  const isRecent = folderKey === 'recent';
  const isTrash = folderKey === 'trash';
  const isFiles = !isTrash && !isRecent;
  const textTrashCount = trash && trashView ? trash.files + trash.folders : 0;
  const classes = {
    button: [
      styles.button,
      isTouch() && styles.buttonTouch,
    ],
  };

  const {isDropping} = useDragDropTarget({
    ref: dndRef,
    targetId: 'trash',
    options: {
      ignoreUploads: true,
    },
  });

  const gotoUpgradePage = useCallback(() => openPage('/upgrade/?utm_source=sidebar&utm_medium=website&utm_content=upgrade_pro&utm_campaign=upgrade_v2_20210816&mf_source=sidebar&mf_medium=website&mf_content=upgrade_pro&mf_campaign=upgrade_v2_20210816'), []);
  const gotoFollowing = useCallback(() => navigate('/folder/following'), []);
  const gotoMyFiles = useCallback(() => navigate('/folder/myfiles'), []);
  const gotoRecent = useCallback(() => navigate('/folder/recent'), []);
  const gotoTrash = useCallback(() => navigate('/folder/trash'), []);

  return (
    <View>
      {!isPromoDisabled && !isPremium && email &&
        <View style={styles.item}>
          <IconButton
            onPress={gotoUpgradePage}
            title={t`Upgrade your account`}
            text={t`Upgrade to Pro`.toUpperCase()}
            textStyle={styles.upsell}
            buttonStyle={[classes.button, styles.upsell] as any}
            buttonHoverStyle={styles.buttonHover}
            icon={
              <Svg viewBox="0 0 20 20" width={20} height={20}>
                <Path d={vectors.icons.pro[0]} fill={colors.secondary.green}/>
                <Path d={vectors.icons.pro[1]} fill={colors.neutral.white}/>
              </Svg>
            }
          />
        </View>
      }
      <View style={styles.item}>
        <IconButton
          onPress={gotoMyFiles}
          title={t`Go to My Files`}
          text={t`My Files`.toUpperCase()}
          textStyle={[styles.text, isFiles && styles.active]}
          buttonStyle={classes.button}
          buttonHoverStyle={styles.buttonHover}
          icon={
            <Svg viewBox="0 0 20 20" width={20} height={20}>
              <Path d={vectors.icons.folder} fill={isFiles ? colors.neutral.white : colors.neutral.mid}/>
            </Svg>
          }
        />
      </View>
      <View style={styles.item}>
        <IconButton
          onPress={gotoRecent}
          title={t`Go to Recent Files`}
          text={t`Recent files`.toUpperCase()}
          textStyle={[styles.text, isRecent && styles.active]}
          buttonStyle={classes.button}
          buttonHoverStyle={styles.buttonHover}
          icon={
            <Svg viewBox="0 0 20 20" width={20} height={20}>
              <Path d={vectors.icons.recent} fill={isRecent ? colors.neutral.white : colors.neutral.mid}/>
            </Svg>
          }
        />
      </View>
      <View style={styles.item}>
        <IconButton
          onPress={gotoFollowing}
          title={t`Go to Shared With Me`}
          text={t`Shared With Me`.toUpperCase()}
          textStyle={[styles.text, isFollowing && styles.active]}
          buttonStyle={classes.button}
          buttonHoverStyle={styles.buttonHover}
          icon={
            <Svg viewBox="0 0 20 20" width={20} height={20}>
              <Path d={vectors.icons.following} fill={isFollowing ? colors.neutral.white : colors.neutral.mid}/>
            </Svg>
          }
        />
      </View>
      <View ref={dndRef} style={styles.item}>
        <IconButton
          onPress={gotoTrash}
          title={t`Go to Trash`}
          text={t`Trash (${trash ? textTrashCount : 0})`.toUpperCase()}
          textStyle={[styles.text, isTrash && styles.active]}
          buttonStyle={classes.button}
          buttonHoverStyle={styles.buttonHover}
          icon={
            <Svg viewBox="0 0 14 20" width={20} height={20}>
              <Path d={vectors.icons.trash} fill={isTrash ? colors.neutral.white : colors.neutral.mid}/>
            </Svg>
          }
        />
        {isDropping && <DropFrame stroke="medium" color={colors.secondary.redOrange} />}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  iconMore: {
    marginTop: 16,
  },
  text: {
    color: colors.neutral.mid,
    fontSize: 12,
  },
  active: {
    color: colors.neutral.white,
  },
  upsell: {
    color: colors.secondary.green,
    fontSize: 12,
  },
  item: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 11,
    marginRight: 8,
  },
  button: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignContent: 'flex-start',
    alignItems: 'center',
    paddingHorizontal: 10,
    lineHeight: 36,
    height: 36,
    width: 178,
    borderRadius: 6,
    color: colors.neutral.mid,
  },
  buttonHover: {
    backgroundColor: colors.neutral.darker,
  },
  buttonTouch: {
    lineHeight: 40,
    height: 40,
  },
});
